/**
 * §1/§2 controlled vocabulary + domain/range, factored out so BOTH the proposal
 * validator (id-prefix typing, at propose time) and the store's confirm path
 * (the objects' STORED types, at promote time) can share one rule set with no
 * import cycle. Pure; imports only types.
 */

import type { ObjectType, RelationType } from "../types.js";
import type { DomainRange } from "./types.js";

export const OBJECT_TYPES: readonly ObjectType[] = [
  "component",
  "problem",
  "decision",
  "rule",
  "action",
  "person",
];

export const RELATION_TYPES: readonly RelationType[] = [
  "causes",
  "affects",
  "depends-on",
  "supersedes",
  "contradicts",
  "resolved-by",
  "covered-by",
  "recurs-as",
  "applies",
  "builds-on",
  "relates-to",
];

/**
 * §2 domain/range. Empty domain/range = "any type". `Guard` is a subtype of
 * `action`, so `resolved-by` ranges over `action`.
 *
 * The decision-cross-reference types (`applies`/`builds-on`/`relates-to`) are
 * deliberately NOT symmetric — `relates-to` in particular spans types
 * (decision↔rule), and `domainRangeViolated()` rejects a symmetric edge whose
 * ends differ in type, so making it symmetric would forbid the very cross-type
 * association it exists to record. Directional-but-generic keeps it legal across
 * types; the extractor canonicalizes nothing for it (each authored direction is
 * its own edge, deduped by relId).
 */
export const DOMAIN_RANGE: Readonly<Record<RelationType, DomainRange>> = {
  "covered-by": { domain: ["problem"], range: ["rule"] },
  "resolved-by": { domain: ["problem"], range: ["action"] },
  "recurs-as": { domain: ["problem"], range: ["problem"] },
  causes: { domain: ["component", "problem", "decision"], range: [] },
  affects: { domain: [], range: ["component"] },
  "depends-on": { domain: ["component"], range: ["component"] },
  supersedes: { domain: ["decision", "rule"], range: ["decision", "rule"] },
  contradicts: { domain: ["decision", "rule"], range: ["decision", "rule"], symmetric: true },
  applies: { domain: ["decision", "action", "rule"], range: ["rule"] },
  "builds-on": { domain: ["decision", "rule"], range: ["decision", "rule"] },
  "relates-to": { domain: [], range: [] },
};

/** Read an object type from a canonical id prefix (`type:rest`). null = untyped/unresolved. */
export function typeFromId(id: string): ObjectType | null {
  const prefix = id.split(":", 1)[0];
  return (OBJECT_TYPES as readonly string[]).includes(prefix) ? (prefix as ObjectType) : null;
}

/**
 * True when (srcType, tgtType) violates `rel`'s domain/range. A null type skips
 * the side it can't determine (propose-time, unresolved ends); the confirm path
 * passes the objects' STORED types (both non-null) so a wrong-type object cannot
 * be promoted even though the id prefix conventionally matched.
 */
export function domainRangeViolated(
  rel: RelationType,
  srcType: ObjectType | null,
  tgtType: ObjectType | null,
): boolean {
  const dr = DOMAIN_RANGE[rel];
  if (srcType && dr.domain.length && !dr.domain.includes(srcType)) return true;
  if (tgtType && dr.range.length && !dr.range.includes(tgtType)) return true;
  if (dr.symmetric && srcType && tgtType && srcType !== tgtType) return true;
  return false;
}
